import { useEffect, useRef, useState } from 'react';

/**
 * Prefs : preferences UI locales au poste (localStorage), partagees par les
 * apps et le shell. Cle stockee sous "taki.prefs.<key>", valeur en JSON.
 *
 * Robustesse : localStorage absent ou en erreur (mode prive, quota) = repli
 * silencieux sur le fallback, jamais d'exception qui remonte a l'app.
 *
 * Synchronisation : chaque `writePref` dispatche un CustomEvent
 * "taki:pref-change" (detail = la cle) sur window ; tous les `usePref` du
 * meme document ecoutent cet event et se resynchronisent.
 */

/**
 * EXPORTES, et c'est le point : le prefixe de stockage et le nom de l'event
 * etaient recopies en litteraux ailleurs (la memoire du "nouveau" balaye les
 * cles par prefixe, `useScreen` ecoute l'event sans passer par `usePref`). Un
 * fait ecrit deux fois est un fait qui divergera : le jour ou l'un des deux
 * change ici, le balayage ne trouverait plus rien et annoncerait quand meme
 * que c'est fait, sans que le build ne bronche. Une seule declaration, donc,
 * et les autres modules l'importent.
 */
export const PREFIX = 'taki.prefs.';
export const CHANGE_EVENT = 'taki:pref-change';

/** Lit une pref. Retourne `fallback` si absente, invalide ou storage indisponible. */
export function readPref<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(PREFIX + key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/**
 * Ecrit une pref (JSON) et notifie le document via "taki:pref-change".
 *
 * ON NE NOTIFIE QUE CE QUI A REELLEMENT ETE ECRIT. Une ecriture qui a echoue
 * n'a rien change, donc personne n'a a se resynchroniser, et annoncer un
 * changement qui n'a pas eu lieu invite chaque ecouteur a RELIRE le stockage
 * et a y trouver l'ancienne valeur, ou rien du tout.
 *
 * Ce n'est pas theorique : avec le dispatch inconditionnel, un poste dont le
 * stockage est indisponible (navigation privee stricte, cookies bloques, quota
 * plein) partait en BOUCLE DE RENDU sur la memoire du "nouveau". L'ecran posait
 * sa date de reference, l'ecriture echouait en silence, l'event partait quand
 * meme, l'ecouteur relisait un stockage vide, ecrasait la date par `null`, et
 * l'effet la reposait aussitot. A l'infini, sans une seule erreur en console.
 */
export function writePref<T>(key: string, value: T): void {
  try {
    window.localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage indisponible : on n'ecrit pas, on ne notifie pas, et rien ne
    // remonte a l'app. L'appelant garde sa valeur en memoire, ce qui est
    // exactement ce que `usePref` fait de son cote.
    return;
  }
  try {
    window.dispatchEvent(new CustomEvent<string>(CHANGE_EVENT, { detail: key }));
  } catch {
    // Environnement sans CustomEvent : pas de resynchronisation, sans casser.
  }
}

/**
 * Hook React : [valeur, setter] sur une pref, a la maniere de useState.
 * Le setter ecrit via `writePref` ; tous les `usePref` du document sur la
 * meme cle se resynchronisent via l'event "taki:pref-change".
 */
export function usePref<T>(key: string, fallback: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => readPref(key, fallback));

  // Le fallback vit dans une ref : un objet recree a chaque rendu ne doit pas
  // rebrancher l'ecouteur.
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  useEffect(() => {
    const onChange = (event: Event): void => {
      if ((event as CustomEvent<string>).detail !== key) return;
      setValue(readPref(key, fallbackRef.current));
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    // Resynchronise si la cle change entre deux rendus.
    setValue(readPref(key, fallbackRef.current));
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, [key]);

  const set = (v: T): void => {
    writePref(key, v);
    // Repli si le storage est indisponible : l'etat local garde la valeur.
    setValue(v);
  };

  return [value, set];
}
